import { DEFAULT_INFERENCE_SETTINGS } from "../user/constants";
/**
 * Get unavailable model IDs based on permissions and model existence
 */
export const getUnavailableModelIds = (inferenceModels, userPermissions, presets) => {
    const existingModelIds = new Set(inferenceModels.map((m) => m.id));
    const unavailableByPermission = new Set(inferenceModels
        .filter((model) => model.permission && !userPermissions?.includes(model.permission))
        .map((model) => model.id));
    const nonExistentModelIds = new Set(presets
        .filter((preset) => !existingModelIds.has(preset.modelId))
        .map((preset) => preset.modelId));
    return new Set([...unavailableByPermission, ...nonExistentModelIds]);
};
/**
 * Filter presets to only those with available models
 */
export const getAvailablePresets = (presets, inferenceModels, unavailableModelIds) => {
    const availableModelIds = new Set(inferenceModels.map((m) => m.id));
    return presets.filter((preset) => availableModelIds.has(preset.modelId) &&
        !unavailableModelIds.has(preset.modelId));
};
/**
 * Create a map of model ID to model name for quick lookup
 */
export const createModelNameMap = (inferenceModels) => {
    return new Map(inferenceModels.map((m) => [m.id, m.name]));
};
/**
 * Get model name by ID from the model name map
 */
export const getModelName = (modelNameMap, modelId) => {
    return modelNameMap.get(modelId) || "Unknown Model";
};
/**
 * Find a preset by ID
 */
export const getPresetById = (presets, presetId) => {
    return presets.find((p) => p.id === presetId);
};
/**
 * Generate a preset name based on settings
 * Only includes parameters that differ from defaults
 * Moved from frontend: presetNameGenerator.ts
 */
export const generatePresetName = (settings, modelName) => {
    const { maxNewTokens, temperature, topP, topK } = settings;
    const modelShortName = modelName.split("/").pop() || modelName;
    const parts = [modelShortName];
    if (temperature !== DEFAULT_INFERENCE_SETTINGS.temperature) {
        parts.push(`${temperature.toFixed(2)}T`);
    }
    if (topP !== DEFAULT_INFERENCE_SETTINGS.topP) {
        parts.push(`${topP.toFixed(2)}P`);
    }
    if (topK !== DEFAULT_INFERENCE_SETTINGS.topK) {
        parts.push(`${topK}K`);
    }
    if (maxNewTokens !== DEFAULT_INFERENCE_SETTINGS.maxNewTokens) {
        parts.push(`${maxNewTokens.toString()}L`);
    }
    return parts.join(" ");
};
/**
 * Check if a preset with identical settings already exists
 * Used for duplicate detection before creating a preset
 */
export const isDuplicatePreset = (existingPresets, settings) => {
    return existingPresets.some((p) => p.modelId === settings.modelId &&
        p.maxNewTokens === settings.maxNewTokens &&
        p.temperature === settings.temperature &&
        p.topP === settings.topP &&
        p.topK === settings.topK);
};
/**
 * Generate a unique preset ID
 */
export const generatePresetId = (modelId) => {
    return `preset_${modelId}_${Math.random().toString(36).substring(2, 9)}`;
};
/**
 * Create preset input from settings
 * Combines all the logic needed to prepare a preset for creation
 */
export const createPresetInput = (settings, modelName, customName) => {
    return {
        id: generatePresetId(settings.modelId),
        name: customName || generatePresetName(settings, modelName),
        modelId: settings.modelId,
        maxNewTokens: settings.maxNewTokens,
        temperature: settings.temperature,
        topP: settings.topP,
        topK: settings.topK,
    };
};
